/**
 * Pending attachments of the HaulierMagic Assistant composer (CU-14zefhy1qnh): screen contexts the user attached with
 * the "Attach current screen context" button and screenshots taken with the camera button. Nothing about the screen is
 * sent unless the user attached it; each context is frozen at click time, so the user can attach several screens by
 * moving between them. Mirrors the RouteMagic module of the same name.
 *
 * No Angular imports here so the helpers can be unit-tested without a TestBed.
 */
import {AssistantScreenContext, contextChipLabel} from 'app/blocks/service/assistant-context/assistant-context';
import {ATTACHED_CONTEXT_MAX_BYTES, capAttachedContext, diagnosticsSummary} from 'app/blocks/service/assistant-context/assistant-diagnostics';

export const MAX_CONTEXTS = 5;
export const MAX_SCREENSHOTS = 3;
/** each `contexts[]` item (label and diagnostics included) */
export const CONTEXT_ITEM_MAX_BYTES = ATTACHED_CONTEXT_MAX_BYTES;
/** screenshot file names the server accepts in `screenshots[]` (same rule as the Java side) */
export const SCREEN_FILE = /^screen-[A-Za-z0-9_-]{1,64}\.(jpg|png)$/;
/** camera-button shots taken before CU-14zefhy1qnh were named `screenshot-<time>.png|jpg` */
const LEGACY_SCREEN_FILE = /^screenshot-\d{1,16}\.(jpg|png)$/;

export interface ContextAttachment {
    kind: 'context';
    /** what makes two attached contexts "the same screen" (see contextKey) */
    key: string;
    label: string;
    /** frozen copy taken at click time; `context.label` = `label` */
    context: AssistantScreenContext;
    /** what the frozen copy carries besides the screen, for the pill tooltip ("2 console errors, 1 failed request") */
    detail?: string;
}

export interface ScreenshotAttachment {
    kind: 'screenshot';
    /** uploaded file name, e.g. `screen-1728460000000.jpg` */
    name: string;
    /** path in the session's file list (for the quiet delete when the pill is removed) */
    path?: string;
    /** "Screenshot n" */
    label: string;
}

export type PendingAttachment = ContextAttachment | ScreenshotAttachment;

/** What a sent message shows as pills (kept in the browser transcript). */
export interface SentAttachment {
    kind: 'context' | 'screenshot';
    label: string;
}

export type AddResult = 'added' | 'duplicate' | 'full' | 'replaced';
/** 'add' = the 🧭 button (same screen = duplicate); 'ask' = "Ask CoPilot" on an error (same screen = refreshed). */
export type AddMode = 'add' | 'ask';

/** The attachment fields of a chat / approve request body; keys are left out when empty. */
export interface TurnAttachments {
    contexts?: AssistantScreenContext[];
    screenshots?: string[];
    /** in-app links hint (`routesHint`), top level: first message of a chat and whenever it changes, context or not */
    routes?: string[];
}

function sorted(values: string[] | undefined): string[] {
    return (values || []).map((v) => String(v)).sort();
}

/**
 * Same url + same screen + same selection / filters + same form state = same context. The trail (recent clicks) is left
 * out on purpose: a click on the 🧭 button itself must not make a second copy of the screen the user is still on.
 */
export function contextKey(ctx: AssistantScreenContext): string {
    if (!ctx) {
        return '';
    }
    const s = ctx.screen || {kind: 'other'};
    const list = s.list || {};
    const form = s.form || {};
    const filters = list.filters
        ? Object.keys(list.filters)
              .sort()
              .map((k) => k + '=' + list.filters[k])
        : [];
    return JSON.stringify([
        ctx.url || '',
        s.kind || 'other',
        s.entity || '',
        s.id || '',
        sorted(list.selectedIds),
        filters,
        list.search || '',
        list.sort || '',
        list.page || 0,
        sorted(form.changed),
        (form.errors || []).map((e) => (e && e.field) + ':' + (e && e.message)).sort()
    ]);
}

/** A capped, labelled copy of a context for `contexts[]` (≤ 6 KB with its label and diagnostics). */
export function capContextItem(ctx: AssistantScreenContext, label: string, maxBytes = CONTEXT_ITEM_MAX_BYTES): AssistantScreenContext {
    const out = capAttachedContext({...ctx, label}, maxBytes);
    out.label = label;
    return out;
}

function countOf(list: PendingAttachment[], kind: PendingAttachment['kind']): number {
    return (list || []).filter((a) => a && a.kind === kind).length;
}

export function canAddContext(list: PendingAttachment[]): boolean {
    return countOf(list, 'context') < MAX_CONTEXTS;
}

export function canAddScreenshot(list: PendingAttachment[]): boolean {
    return countOf(list, 'screenshot') < MAX_SCREENSHOTS;
}

/** Screenshots are numbered 1..n in the order they were taken. */
function renumber(list: PendingAttachment[]): PendingAttachment[] {
    let n = 0;
    return list.map((a) => (a.kind === 'screenshot' ? {...a, label: 'Screenshot ' + ++n} : a));
}

/** A frozen, capped pill for `ctx` with its tooltip detail. */
function contextPill(ctx: AssistantScreenContext, key: string): ContextAttachment {
    const label = contextChipLabel(ctx) || 'Screen';
    const context = capContextItem(ctx, label);
    const item: ContextAttachment = {kind: 'context', key, label, context};
    const detail = diagnosticsSummary(context);
    if (detail) {
        item.detail = detail;
    }
    return item;
}

/**
 * Freezes the current screen as a new pill; the same screen twice is a duplicate, the 6th context is refused.
 *
 * `mode: 'ask'` ("Ask CoPilot" on an error): the error happened after the screen may already have been attached, and
 * errors are not part of `contextKey`, so a same-screen pill is REPLACED (in place, even when the list is full) by the
 * new snapshot that carries the error, the console errors and the failed requests (`result: 'replaced'`).
 */
export function addContext(
    list: PendingAttachment[],
    ctx: AssistantScreenContext,
    mode: AddMode = 'add'
): {list: PendingAttachment[]; result: AddResult} {
    const current = list || [];
    if (!ctx) {
        return {list: current, result: 'duplicate'};
    }
    const key = contextKey(ctx);
    const same = current.findIndex((a) => a.kind === 'context' && a.key === key);
    if (same >= 0) {
        if (mode !== 'ask') {
            return {list: current, result: 'duplicate'};
        }
        const next = current.slice();
        next[same] = contextPill(ctx, key);
        return {list: next, result: 'replaced'};
    }
    if (!canAddContext(current)) {
        return {list: current, result: 'full'};
    }
    return {list: current.concat(contextPill(ctx, key)), result: 'added'};
}

/** Adds an uploaded screenshot (labels renumbered); the 4th one is refused. */
export function addScreenshot(list: PendingAttachment[], name: string, path?: string): {list: PendingAttachment[]; result: AddResult} {
    const current = list || [];
    if (!name || current.some((a) => a.kind === 'screenshot' && a.name === name)) {
        return {list: current, result: 'duplicate'};
    }
    if (!canAddScreenshot(current)) {
        return {list: current, result: 'full'};
    }
    const item: ScreenshotAttachment = {kind: 'screenshot', name, label: ''};
    if (path) {
        item.path = path;
    }
    return {list: renumber(current.concat(item)), result: 'added'};
}

export function removeAttachment(list: PendingAttachment[], item: PendingAttachment): PendingAttachment[] {
    return renumber((list || []).filter((a) => a !== item));
}

/** `{contexts, screenshots}` for the request body; both keys left out when nothing is attached. */
export function turnPayload(list: PendingAttachment[]): TurnAttachments {
    const out: TurnAttachments = {};
    const contexts = (list || []).filter((a): a is ContextAttachment => a.kind === 'context').map((a) => a.context);
    const screenshots = (list || []).filter((a): a is ScreenshotAttachment => a.kind === 'screenshot').map((a) => a.name);
    if (contexts.length) {
        out.contexts = contexts;
    }
    if (screenshots.length) {
        out.screenshots = screenshots;
    }
    return out;
}

/** The pills a sent message keeps in the transcript. */
export function sentAttachments(list: PendingAttachment[]): SentAttachment[] {
    return (list || []).map((a) => ({kind: a.kind, label: a.label}));
}

/**
 * Adds the in-app links hint as the top-level `routes` field (lead decision 1: never inside a context, sent whether or
 * not a context is attached). Returns a new payload, the input is not changed.
 */
export function withRoutesHint(payload: TurnAttachments, routes: string[] | null): TurnAttachments {
    if (!routes || !routes.length) {
        return payload;
    }
    return {...(payload || {}), routes: routes.slice()};
}

/** Base name of a session file entry (`{path}` or a plain string). */
export function baseName(file: any): string {
    const path = (file && file.path) || String(file || '');
    return path.includes('/') ? path.substring(path.lastIndexOf('/') + 1) : path;
}

/** Screenshots (camera button, answered screen requests, and the older camera shots) never show in "Attach files". */
export function isScreenFile(file: any): boolean {
    const name = baseName(file);
    return SCREEN_FILE.test(name) || LEGACY_SCREEN_FILE.test(name);
}
